import type Auth from "@/utils/auth";
import type { AxiosResponse } from "axios";

export namespace IApp {
  export type ICreateData<T> = Omit<T, "id" | "createdAt" | "updatedAt"> & { id?: string };
  export enum IFeedTypes {
    GoogleSearchConsole = "GOOGLE_SEARCH_CONSOLE",
    BirdEye = "BIRD_EYE",
    GoogleAnalytics = "GOOGLE_ANALYTICS"
  }
  export enum IEnvironments {
    Local = "local",
    Dev = "dev",
    Staging = "staging",
    Prod = "prod"
  }
  export interface IUser {
    id: string;
    email: string;
    name: string;
    enabled: boolean;
    groups: Auth.UserGroup[];
    status?: string;
    permissions: Array<{
      productId: string;
      permissions: Auth.Permission[];
      isDefaultsIncluded: boolean;
    }>;
  }

  export interface IAccount {
    feed: IFeedTypes;
    account: string;
    customerId: string;
    name: string;
    data: any;
    status: IAccountStatus;
    isSynced?: boolean;
    syncedPercent?: number;
  }

  export interface IModel {
    createdAt?: string;
    updatedAt?: string;
  }

  export enum ReportStatus {
    Pending = "Pending",
    GeneratingSummary = "GeneratingSummary",
    Reviewing = "Reviewing",
    InProgress = "InProgress",
    Completed = "Completed",
    Confirmed = "Confirmed",
    Failed = "Failed"
  }

  export interface IReport extends IModel {
    id: string;
    requestId: string;
    title?: string;
    dateRange: {
      start: string;
      end: string;
    };
    comparedRange: {
      start: string;
      end: string;
    };
    customerId: string;
    customerName?: string;
    status: ReportStatus;
    downloadId?: string;
    createdBy: string;
    summary?: string;
    showSummary?: boolean;
    promptId?: string | null;
    summaryCreatedAt?: string;
    summaryUpdatedAt?: string;
  }

  export interface IProfile {
    userId: string;
    defaultPromptId?: string | null;
    createdAt?: string;
    updatedAt?: string;
  }

  export interface IPrompt {
    id: string;
    title: string;
    content: string;
    createdBy?: string;
    updatedBy?: string;
  }

  export interface IReportSummary {
    sourceId: string;
    summary: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string;
  }

  export interface MonthRange {
    start: string;
    end: string;
  }

  export enum PrintMode {
    Edit = "edit",
    View = "view"
  }

  export interface IGoogleSearchConsoleAccount {
    feed: IFeedTypes;
    account: string;
    customerId: string;
    name: string;
    data: {
      siteUrls: string[];
    };
    status: IAccountStatus;
    syncs: Sync[];
  }

  export interface IBirdEyeAccount {
    feed: IFeedTypes;
    account: string;
    customerId: string;
    name: string;
    status: IAccountStatus;
    data: {
      businessId: string;
      apiKey: string;
    };
    syncs: Sync[];
  }

  export interface IGoogleAnalyticsAccount {
    feed: IFeedTypes;
    account: string;
    customerId: string;
    name: string;
    data: {
      accountId?: string;
      propertyId?: string;
    };
    meta?: {
      isAccountListFetched?: boolean;
    };
    status: IAccountStatus;
    syncs: Sync[];
  }

  export interface IGoogleAnalyticsAdminAccount {
    name: string;
    createTime: string;
    updateTime: string;
    displayName: string;
    regionCode: string;
    gmpOrganization: string;
  }

  export enum ILoadingStatus {
    Idle = "idle",
    Loading = "loading",
    Success = "success",
    Error = "error"
  }

  export enum Device {
    Desktop = "DESKTOP",
    Mobile = "MOBILE",
    Tablet = "TABLET"
  }

  export enum IAccountStatus {
    Active = "active",
    Archived = "archived",
    Paused = "paused",
    Deleted = "deleted"
  }

  export enum DataType {
    Default = "default",
    BirdEyeReviews = "reviews",
    BirdEyeListings = "listings",
    GoogleAnalyticsTotal = "google-analytics-total",
    GoogleAnalyticsPageViews = "google-analytics-page-views",
    GoogleAnalyticsSessionCount = "google-analytics-session-count",
    GoogleAnalyticsCityActiveUsers = "google-analytics-city-active-users",
    GoogleAnalyticsEventCount = "google-analytics-event-count",
    GoogleAnalyticsBrowserDeviceEventActiveUsers = "google-analytics-browser-device-event-active-users",
    GoogleAnalyticsDeviceActiveUsers = "google-analytics-device-active-users",
    GoogleAnalyticsOrganicTotal = "google-analytics-organic-total",
    GoogleAnalyticsOrganicCityActiveUsers = "google-analytics-organic-city-active-users",
    GoogleAnalyticsOrganicDeviceActiveUsers = "google-analytics-organic-device-active-users"
  }

  export enum SyncStatus {
    NotStarted = "not-started",
    InProgress = "in-progress",
    Completed = "completed",
    Failed = "failed"
  }

  export interface Sync {
    dataType: DataType;
    month: string;
    startedAt?: string;
    completedAt?: string;
    failedAt?: string;
    status: SyncStatus;
    errorMessage?: string;
  }

  export interface ICustomer {
    id: string;
    name: string;
    accounts?: IAccount[];
    isSynced?: boolean;
    createdAt: string;
    updatedAt?: string;
    deletedAt?: string;
  }
  export interface ChartSeriesItem {
    name?: string;
    type: "line" | "column";
    color?: string;
    group?: string;
    hidden?: boolean;
    zIndex?: number;
    data: Array<number | null>;
  }
  export type IApiResponse<T> =
    | {
        status: true;
        data: T;
        headers: AxiosResponse["headers"];
        message?: string;
        title?: string;
        type?: string;
      }
    | {
        status: false;
        message: string;
        data?: T;
        title: string;
        type: string;
      };

  export enum IGoogleAnalyticsAnalyticsTotalMetrics {
    TotalUsers = "totalUsers",
    NewUsers = "newUsers",
    EventCount = "eventCount",
    ScreenPageViews = "screenPageViews",
    ScreenPageViewsPerSession = "screenPageViewsPerSession",
    Sessions = "sessions",
    EventsPerSession = "eventsPerSession",
    EngagedSessions = "engagedSessions",
    EngagementRate = "engagementRate",
    AverageSessionDuration = "averageSessionDuration"
  }

  type SelectItemKey<T = Record<string, any>> =
    | boolean
    | null
    | undefined
    | string
    | readonly (string | number)[]
    | ((item: T, fallback?: any) => any);

  export type DataTableHeader<T = Record<string, any>> = {
    key?: "data-table-group" | "data-table-select" | "data-table-expand" | (string & {});
    value?: SelectItemKey<T>;
    title?: string;
    fixed?: boolean;
    align?: "start" | "end" | "center";
    width?: number | string;
    minWidth?: string;
    maxWidth?: string;
    nowrap?: boolean;
    headerProps?: Record<string, any>;
    cellProps?: Record<string, any>;
    sortable?: boolean;
    children?: DataTableHeader<T>[];
  };
  export interface Content {
    privacyPolicy: string;
    termsOfService: string;
    reportPrompt: string;
  }

  export interface IUsageRangeItem {
    month: string;
    module: string;
    product: string;
    bedrockInputTokens: number;
    bedrockOutputTokens: number;
    productModule: string;
  }

  export interface IUsageRangeTotals {
    bedrockInputTokens?: number;
    bedrockOutputTokens?: number;
    costTotalUsd?: number;
  }

  export interface IUsageRangeResponse {
    accountId: string;
    startMonth: string;
    endMonth: string;
    months: string[];
    items: IUsageRangeItem[];
    totals: IUsageRangeTotals;
  }
}

export namespace IAnalyticsData {
  interface ISearchMetrics {
    month: string;
    clicks: number;
    impressions: number;
    ctr: string;
    position: number;
  }

  interface ISearchChange {
    clicks: number;
    impressions: number;
    ctr: string;
    position: number;
  }
  export interface IGoogleSearchConsoleQueryPage {
    item: string;
    curr: ISearchMetrics;
    compared: ISearchMetrics;
    change: ISearchChange;
  }

  export interface IGoogleSearchConsoleDeviceTotal {
    curr: ISearchDeviceTotalMetics;
    compared: ISearchDeviceTotalMetics;
  }
  export interface IGoogleSearchConsoleAnalyticsImpressionPositionClickItem {
    month: string;
    clicks: number;
    impressions: number;
    position: number;
  }

  export type IGoogleSearchConsoleAnalyticsImpressionPositionClick =
    Array<IGoogleSearchConsoleAnalyticsImpressionPositionClickItem>;

  export interface ISearchDeviceTotalMetics {
    clicks: number;
    ctr: string;
    device: IApp.Device;
    impressions: number;
  }

  export interface IGoogleSearchConsoleQuery {
    query: string;
    curr: ISearchMetrics;
    compared: ISearchMetrics;
    change: ISearchChange;
  }

  export interface IGoogleSearchConsolePage {
    page: string;
    curr: ISearchMetrics;
    compared: ISearchMetrics;
    change: ISearchChange;
  }

  export enum GoogleSearchConsoleAnalyticsType {
    Query = "query",
    Page = "page",
    DeviceTotal = "device-total",
    ImpressionPositionClick = "impression-position-click"
  }

  export enum BirdEyeAnalyticsType {
    ReviewsAndRatings = "reviews-and-ratings",
    ReviewsDistribution = "reviews-distribution",
    ResponseRate = "response-rate",
    ReviewsAndRatingsBySource = "reviews-and-ratings-by-source",
    Listings = "listings",
    DirectoryListings = "directory-listings"
  }

  export enum GoogleAnalyticsType {
    Total = "total",
    PageViews = "page-views",
    SessionCount = "session-count",
    EngagedSessions = "engaged-sessions",
    MonthlyCityActiveUsers = "monthly-city-active-users",
    CityActiveUsers = "city-active-users",
    EventCount = "event-count",
    MonthlyEventCount = "monthly-event-count",
    BrowserDeviceEventActiveUsers = "browser-device-event-active-users",
    DeviceActiveUsers = "device-active-users",
    MonthlyDeviceActiveUsers = "monthly-device-active-users",
    OrganicTotal = "organic-total",
    OrganicCityActiveUsers = "organic-city-active-users",
    OrganicDeviceActiveUsers = "organic-device-active-users"
  }

  export interface IBirdEyeReviewsAndRatingsAnalyze {
    reviews: number;
    rating: number;
    month: string;
  }

  export interface IBirdEyeReviewsDistributionAnalyze {
    month: string;
    reviews: {
      rating: number;
      count: number;
    }[];
  }

  export interface IBirdEyeResponseRateAnalyze {
    month: string;
    total: number;
    response: number;
    rating: number;
  }

  export interface IBirdEyeReviewsAndRatingsBySourceAnalyze {
    source: "Google" | "Facebook";
    reviews: number;
    ratings: number;
  }

  export interface IBirdEyeListingsAnalyze {
    month: string;
    google: {
      impressions: {
        map: {
          desktop: number;
          mobile: number;
        };
        search: {
          desktop: number;
          mobile: number;
        };
      };
      visits: number;
      direction_requests: number;
      calls: number;
    };
    facebook: {
      users_reached: number;
      users_engaged: number;
    };
    bing: {
      users_reached: number;
      calls: number;
      direction_requests: number;
      visits: number;
    };
  }

  export interface IBirdEyeDirectoryListings {
    businessName: string;
    phone: string;
    businessSmsNumber: string;
    location: {
      address: string;
      city: string;
      state: string;
      stateName: string;
      zip: string;
      countryCode: string;
      countryName: string;
      lat: string;
      lng: string;
    };
    total: number;
    synced: number;
    submitted: number;
    needUpdates: number;
    optedOut: number;
    notConnected: number;
    sites: Array<{
      siteName: string;
      status: "Synced" | "Submitted" | "Need Updates" | "Action Required" | "Connect" | "Opted out";
      duplicateListingsCount: number;
    }>;
  }
}

export namespace IApiRequest {
  export interface Pagination {
    page: number;
    limit?: number;
  }

  export interface GoogleSearchConsoleAnalyticsBase {
    type: IAnalyticsData.GoogleSearchConsoleAnalyticsType;
  }

  export interface GoogleSearchConsoleAnalyticsQuery extends GoogleSearchConsoleAnalyticsBase {
    type: IAnalyticsData.GoogleSearchConsoleAnalyticsType.Query;
    monthRange: IApp.MonthRange;
    comparedMonthRange: IApp.MonthRange;
    device?: IApp.Device;
  }

  export interface GoogleSearchConsoleAnalyticsPage extends GoogleSearchConsoleAnalyticsBase {
    type: IAnalyticsData.GoogleSearchConsoleAnalyticsType.Page;
    monthRange: IApp.MonthRange;
    comparedMonthRange: IApp.MonthRange;
    device?: IApp.Device;
  }

  export interface GoogleSearchConsoleAnalyticsImpressionPositionClick
    extends GoogleSearchConsoleAnalyticsBase {
    type: IAnalyticsData.GoogleSearchConsoleAnalyticsType.ImpressionPositionClick;
    monthRange: IApp.MonthRange;
    device?: IApp.Device;
  }

  export interface GoogleSearchConsoleAnalyticsDeviceTotal
    extends GoogleSearchConsoleAnalyticsBase {
    type: IAnalyticsData.GoogleSearchConsoleAnalyticsType.DeviceTotal;
    monthRange: IApp.MonthRange;
    comparedMonthRange: IApp.MonthRange;
    device?: IApp.Device;
  }

  export interface PatchAccountInfo {
    type: IApp.IFeedTypes;
  }

  export interface PatchGoogleSearchConsoleAccountInfo extends PatchAccountInfo {
    type: IApp.IFeedTypes.GoogleSearchConsole;
    name?: string;
    siteUrls?: string[];
  }

  export interface PatchBirdEyeAccountInfo extends PatchAccountInfo {
    type: IApp.IFeedTypes.BirdEye;
    name?: string;
    businessId?: string;
    apiKey?: string;
  }

  export interface PatchGoogleAnalyticsAccountInfo extends PatchAccountInfo {
    type: IApp.IFeedTypes.GoogleAnalytics;
    name?: string;
    accountId?: string;
    propertyId?: string;
  }

  export interface IBirdEyeAnalyticsBase {
    type: IAnalyticsData.BirdEyeAnalyticsType;
  }

  export interface BirdEyeDefaultInput extends IBirdEyeAnalyticsBase {
    type: IAnalyticsData.BirdEyeAnalyticsType;
    startMonth: string;
    endMonth: string;
  }

  export interface BirdEyeDirectoryListingInput extends IBirdEyeAnalyticsBase {
    type: IAnalyticsData.BirdEyeAnalyticsType.DirectoryListings;
  }

  export type BirdEyeAnalyzeInput = BirdEyeDefaultInput | BirdEyeDirectoryListingInput;

  export interface GoogleAnalyticsDefaultInput {
    monthRange: IApp.MonthRange;
  }

  export interface GoogleAnalyticsTotalInput {
    type: IAnalyticsData.GoogleAnalyticsType.Total;
    monthRange: IApp.MonthRange;
  }

  export interface GoogleAnalyticsPageViewsInput extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.PageViews;
    comparedMonthRange: IApp.MonthRange;
  }

  export interface GoogleAnalyticsSessionCountInput extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.SessionCount;
    comparedMonthRange: IApp.MonthRange;
  }

  export interface GoogleAnalyticsEngagedSessionsInput extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.EngagedSessions;
  }

  export interface GoogleAnalyticsCityActiveUsersMonthlyInput extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.MonthlyCityActiveUsers;
    comparedMonthRange: IApp.MonthRange;
  }

  export interface GoogleAnalyticsEventCountInput extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.EventCount;
    comparedMonthRange: IApp.MonthRange;
  }

  export interface GoogleAnalyticsMonthlyEventCountInput extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.MonthlyEventCount;
    comparedMonthRange: IApp.MonthRange;
  }

  export interface GoogleAnalyticsMonthlyDeviceActiveUsersInput
    extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.MonthlyDeviceActiveUsers;
  }

  export interface GoogleAnalyticsOrganicTotalInput extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.OrganicTotal;
    metrics: Google.GoogleAnalyticsOrganicTotalMetrics[];
  }

  export interface GoogleAnalyticsOrganicCityActiveUsersMonthlyInput
    extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.OrganicCityActiveUsers;
    comparedMonthRange: IApp.MonthRange;
  }

  export interface GoogleAnalyticsMonthlyOrganicDeviceActiveUsersInput
    extends GoogleAnalyticsDefaultInput {
    type: IAnalyticsData.GoogleAnalyticsType.OrganicDeviceActiveUsers;
  }

  export interface GoogleAnalyticsTotalOutputItem {
    month: string;
    counts: {
      totalUsers: number;
      newUsers: number;
      eventCount: number;
      screenPageViews: number;
      screenPageViewsPerSession: number;
      sessions: number;
      eventsPerSession: number;
      engagedSessions: number;
      engagementRate: number;
      averageSessionDuration: number;
    };
  }

  export type GoogleAnalyticsTotalOutput = Array<GoogleAnalyticsTotalOutputItem>;

  export interface GoogleAnalyticsPageViewsOutputItem {
    pagePath: string;
    pageTitle: string;
    curr: {
      screenPageViews: number;
    };
    compared: {
      screenPageViews: number;
    };
  }

  export type GoogleAnalyticsPageViewsOutput = Array<GoogleAnalyticsPageViewsOutputItem>;

  export interface GoogleAnalyticsSessionCountOutputItem {
    sessionSource: string;
    sessionSourceMedium: string;
    curr: {
      sessions: number;
      totalSessions: number;
      eventCount: number;
      eventsPerSession: number;
    };
    compared: {
      sessions: number;
      totalSessions: number;
      eventCount: number;
      eventsPerSession: number;
    };
  }

  export type GoogleAnalyticsSessionCountOutput = Array<GoogleAnalyticsSessionCountOutputItem>;

  export interface GoogleAnalyticsEngagedSessionsItem {
    engagedSessions: number;
    month: string;
  }

  export type GoogleAnalyticsEngagedSessionsOutput = Array<GoogleAnalyticsEngagedSessionsItem>;

  export interface GoogleAnalyticsCityActiveUsersMonthlyOutputItem {
    country: string;
    city: string;
    curr: {
      activeUsers: Array<{
        month: string;
        value: number;
      }>;
    };
    compared: {
      activeUsers: Array<{
        month: string;
        value: number;
      }>;
    };
  }
  export type GoogleAnalyticsCityActiveUsersMonthlyOutput =
    Array<GoogleAnalyticsCityActiveUsersMonthlyOutputItem>;

  export interface GoogleAnalyticsEventCountItem {
    eventName: string;
    curr: {
      eventCount: number;
      totalEvents: number;
    };
    compared: {
      eventCount: number;
      totalEvents: number;
    };
  }

  export type GoogleAnalyticsEventCountOutput = Array<GoogleAnalyticsEventCountItem>;

  export interface GoogleAnalyticsEventCountMonthlyOutputItem {
    eventName: string;
    curr: {
      counts: Array<{
        month: string;
        value: number;
      }>;
    };
    compared: {
      counts: Array<{
        month: string;
        value: number;
      }>;
    };
  }

  export type GoogleAnalyticsMonthlyEventCountOutput =
    Array<GoogleAnalyticsEventCountMonthlyOutputItem>;
  export interface GoogleAnalyticsMonthlyDeviceActiveUsersOutputItem {
    device: string;
    activeUsers: Array<{
      month: string;
      value: number;
    }>;
  }
  export type GoogleAnalyticsMonthlyDeviceActiveUsersOutput =
    Array<GoogleAnalyticsMonthlyDeviceActiveUsersOutputItem>;

  export interface GoogleAnalyticsOrganicTotalMonthlyOutputItem {
    metric: Google.GoogleAnalyticsOrganicTotalMetrics;
    count: Array<{
      month: string;
      value: number;
    }>;
  }

  export type GoogleAnalyticsOrganicTotalMonthlyOutput =
    Array<GoogleAnalyticsOrganicTotalMonthlyOutputItem>;

  export interface ReportCreateInput {
    customerIds: string[];
    dateRange: IApp.MonthRange;
    comparedRange: IApp.MonthRange;
    showSummary: boolean;
    summary?: string;
    promptId?: string | null;
  }

  export interface ReportSummariesOutput {
    summaries: Array<IApp.IReportSummary>;
  }
}

export namespace Google {
  export enum GoogleAnalyticsOrganicTotalMetrics {
    ActiveUsers = "active-users",
    NewUsers = "new-users",
    TotalUsers = "total-users",
    EngagementRate = "engagement-rate",
    UserEngagementDuration = "user-engagement-duration",
    EngagedSessions = "engaged-sessions",
    EventCount = "event-count",
    EventsPerSession = "events-per-session",
    Sessions = "sessions"
  }
  export interface GoogleAnalyticsAdminAccountProperty {
    name: string;
    parent: string;
    createTime: string;
    updateTime: string;
    displayName: string;
    timeZone: string;
    currencyCode: string;
    serviceLevel: string;
    account: string;
    propertyType: string;
  }
}
